/**
 * **Ranged or melee, per attack**, for a weapon usable either way (`WeaponProfile.eitherMode`,
 * the Ram Cannon — Core p.297: "This lance can be used as either a ranged or melee weapon, but
 * not both in the same turn").
 *
 * {@link isRangedAttack} answers from the range SHAPE, which for `[Range 5, Threat 2]` is always
 * "ranged". That made every adjacent swing pay Engaged's ranged +1 Difficulty and let the target
 * claim cover, which RAW denies to melee (p.66). {@link isRangedWeaponAttack} is the one answer
 * every attack-fact site reads instead: the S2 `rangedAttack` fact (Engaged, `match.ranged`, the
 * damage window's `ranged` for Rebound), the S2.5 cover fold, and the AI's EV preview.
 *
 * The mode (`npc-features-fielded-roster` fork F2): the intent's explicit `mode` wins; absent, the
 * attack is melee when the target is within the weapon's Threat and ranged beyond it. An
 * unmeasurable target (unplaced) falls back to the range shape, which is ranged.
 */

import type { Cell, Combatant, EncounterState, ResourceDeltaStep, WeaponMode, WeaponProfile } from "@lancer-vtt/domain";
import { encodeWeaponModeLatch, isRangedAttack, isRangedAttackAt, threatValue, weaponInstanceKey, weaponModeLatchKey } from "@lancer-vtt/domain";
import type { AttackTarget, RequestAttack } from "@lancer-vtt/shared";
import { combatantCellRange, combatantRange } from "../effects/spatialAnswerer";
import { selectWeapon } from "./weapons";

/** Args for {@link attackWeaponMode} (max-params-3 discipline, `CODE_STYLE.md`). */
export type AttackWeaponModeArgs = {
  state: EncounterState;
  attacker: Combatant;
  /** The attack's target and optional explicit mode — any `RequestAttack` fits. */
  intent: { target: AttackTarget; mode?: WeaponMode };
  weapon: WeaponProfile;
};

/** The distance from `attacker` to the attack's target, or `null` when either is unplaced. */
export function attackTargetDistance(args: Pick<AttackWeaponModeArgs, "state" | "attacker" | "intent">): number | null {
  const { state, attacker, intent } = args;
  return intent.target.kind === "unit"
    ? combatantRange(state, attacker.id, intent.target.id)
    : combatantCellRange(state, attacker.id, intent.target.cell as Cell);
}

/**
 * The resolved mode of an attack with an {@link WeaponProfile.eitherMode} weapon, or `undefined`
 * for any other weapon (its range shape decides, unchanged).
 */
export function attackWeaponMode(args: AttackWeaponModeArgs): WeaponMode | undefined {
  const { intent, weapon } = args;
  if (weapon.eitherMode !== true) return undefined;
  if (intent.mode !== undefined) return intent.mode;
  const distance = attackTargetDistance(args);
  if (distance === null) return isRangedAttack(weapon.range) ? "ranged" : "melee";
  return distance <= threatValue(weapon.range ?? []) ? "melee" : "ranged";
}

/**
 * Is this weapon attack ranged? The mode for an either-mode weapon, else the range at the target's
 * distance ({@link isRangedAttackAt}: a Threat-first weapon with a Range, the unset Engineer
 * prototype, is melee only within its Threat). Weapon-shape only: callers still exempt tech
 * attacks themselves, exactly as they did around `isRangedAttack`.
 */
export function isRangedWeaponAttack(args: AttackWeaponModeArgs): boolean {
  const mode = attackWeaponMode(args);
  return mode !== undefined ? mode === "ranged" : isRangedAttackAt(args.weapon.range, attackTargetDistance(args));
}

/**
 * The step that stamps this attack's mode onto the weapon's per-turn latch
 * (`WEAPON_MODE_LATCH_PREFIX`, `@lancer-vtt/domain`), or `undefined` when there is nothing to
 * stamp: a weapon with one mode, or a latch that already reads this mode for this turn. Folded
 * into the resolution's command by `refereeIntent` beside the attack-ordinal increment, so it
 * undoes with the attack. A GM `gm_force` shot stamps it too — the override still fires the weapon
 * one way.
 */
export function weaponModeLatchStep(args: { state: EncounterState; intent: RequestAttack }): ResourceDeltaStep | undefined {
  const { state, intent } = args;
  const attacker = state.combatants[intent.attacker];
  if (!attacker) return undefined;
  const weapon = selectWeapon(attacker, intent.weapon, intent.profile);
  const mode = attackWeaponMode({ state, attacker, intent, weapon });
  if (mode === undefined) return undefined;
  const key = weaponModeLatchKey(weaponInstanceKey(weapon));
  const want = encodeWeaponModeLatch(state.activation.turnOrdinal, mode);
  const have = attacker.current.resources?.[key] ?? 0;
  return want === have ? undefined : { op: "resource_delta", target: attacker.id, key, delta: want - have };
}
